/** Édition du profil existant + gestion des notifications push. */
import { useState } from 'react';
import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import { ProfileForm, type ProfileFormValues } from '@/components/ProfileForm';
import { currentWeight, type Profile } from '@/domain';
import { useProfile, useUpsertProfile, useWeights } from '@/hooks/queries';
import { usePush } from '@/hooks/usePush';

// Profil (domaine, camelCase) → valeurs de formulaire.
function toFormValues(p: Profile): ProfileFormValues {
  return {
    birthdate: p.birthdate,
    height_cm: p.heightCm,
    sex: p.sex,
    activity_level: p.activityLevel,
    ideal_weight_kg: p.idealWeightKg,
    target_weight_kg: p.targetWeightKg,
    calorie_threshold: p.calorieThreshold,
    threshold_mode: p.thresholdMode,
    protein_target_g: p.proteinTargetG,
    protein_target_mode: p.proteinTargetMode,
    protein_per_kg: p.proteinPerKg,
    email_reminders: p.emailReminders,
    push_reminders: p.pushReminders,
    macro_target_mode: p.macroTargetMode,
    fat_pct: p.fatPct,
    carb_target_g: p.carbTargetG,
    fat_target_g: p.fatTargetG,
    water_target_ml: p.waterTargetMl,
    water_target_mode: p.waterTargetMode,
    water_ml_per_kg: p.waterMlPerKg,
    weekly_loss_kg: p.weeklyLossKg,
    training_phase: p.trainingPhase,
    surplus_kcal: p.surplusKcal,
  };
}

export function ProfilePage() {
  const { data: profile } = useProfile();
  const { data: weights } = useWeights();
  const upsert = useUpsertProfile();
  const push = usePush();
  const [showDelete, setShowDelete] = useState(false);

  if (!profile) return null;
  const cw = weights?.length ? currentWeight(weights) : undefined;
  const formValues = toFormValues(profile.profile);

  // Active/désactive le push et synchronise le flag du profil.
  async function togglePush(enable: boolean) {
    if (enable) await push.subscribe();
    else await push.unsubscribe();
    await upsert.mutateAsync({
      id: profile!.id,
      values: { ...formValues, push_reminders: enable },
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Profil</h1>
          <p>Ajuste tes données ; les objectifs sont recalculés en direct.</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 680, marginBottom: '1rem' }}>
        <ProfileForm
          initial={{ ...formValues, currentWeightKg: cw }}
          submitLabel="Enregistrer"
          onSubmit={async (values) => {
            await upsert.mutateAsync({ id: profile.id, values });
          }}
        />
      </div>

      <div className="card" style={{ maxWidth: 680 }}>
        <h3>Notifications push</h3>
        {!push.supported ? (
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Le push web n'est pas disponible ici. Sur iPhone, installe d'abord l'application sur
            l'écran d'accueil (PWA) puis reviens activer.
          </p>
        ) : (
          <>
            <p className="muted" style={{ fontSize: '0.85rem' }}>
              Reçois un rappel push si tu n'as pas pesé dans la journée
              {push.permission === 'denied' && ' — permission bloquée dans le navigateur.'}
            </p>
            <div className="toolbar" style={{ margin: 0 }}>
              {push.subscribed ? (
                <button
                  type="button"
                  className="ghost"
                  disabled={push.busy}
                  onClick={() => togglePush(false)}
                >
                  Désactiver
                </button>
              ) : (
                <button
                  type="button"
                  disabled={push.busy || push.permission === 'denied'}
                  onClick={() => togglePush(true)}
                >
                  Activer les notifications
                </button>
              )}
            </div>
            {push.error && <p className="error">{push.error}</p>}
          </>
        )}
      </div>

      <div className="card" style={{ maxWidth: 680, marginTop: '1rem' }}>
        <h3>Zone de danger</h3>
        <p className="muted" style={{ fontSize: '0.85rem' }}>
          Supprimer ton compte efface définitivement toutes tes données (poids, calories,
          hydratation, séances, aliments…). Action irréversible.
        </p>
        <div className="toolbar" style={{ margin: 0 }}>
          <button type="button" className="danger" onClick={() => setShowDelete(true)}>
            Supprimer mon compte
          </button>
        </div>
      </div>

      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} />}
    </>
  );
}
